import { useInfiniteQuery } from '@tanstack/react-query';
import { apiGet, type ApiError } from '../lib/api';
import { AUDITED_READ } from '../lib/query-client';
import {
  FEEDBACK_LABELS,
  SUBSCRIPTION_STATUSES,
  UTC_DAY_PATTERN,
  UUID_PATTERN,
  type ClientMetaKey,
} from '../lib/api-enums';

export interface AdminFeedbackRow {
  id: string;
  created_at: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  is_sim: boolean;
  body: string;
  voice_used: boolean;
  platform: string;
  app_version: string | null;
  build_number: string | null;
  subscription_status: string;
  client_meta: Partial<Record<ClientMetaKey, string>>;
  sentiment: string | null;
  sentiment_score: number | null;
  kind: string | null;
  surface: string | null;
  actionability: string | null;
  classified_at: string | null;
  classifier_prompt_version: string | null;
}

export interface FeedbackPage {
  rows: AdminFeedbackRow[];
  next_cursor: string | null;
}

const UTC_DAY_RE = new RegExp(UTC_DAY_PATTERN);
const UUID_RE = new RegExp(UUID_PATTERN);

const oneOf = (values: readonly string[]) => (value: string) => values.includes(value);

/** A 'YYYY-MM-DD' day that is on the calendar: '2026-02-30' and '2026-13-01' are not. */
function isUtcDay(value: string): boolean {
  if (!UTC_DAY_RE.test(value)) return false;
  const day = new Date(`${value}T00:00:00Z`);
  // An impossible month makes an invalid Date, whose toISOString() throws.
  return !Number.isNaN(day.getTime()) && day.toISOString().startsWith(value);
}

/**
 * The filters, under GET /admin/feedback's own parameter names and in their URL order, each
 * with the check its value must pass. The URL's query and the request's are the same.
 */
const PARAMS = {
  kind: oneOf(FEEDBACK_LABELS.kind),
  surface: oneOf(FEEDBACK_LABELS.surface),
  sentiment: oneOf(FEEDBACK_LABELS.sentiment),
  subscription_status: oneOf(SUBSCRIPTION_STATUSES),
  unclassified: (value: string) => value === 'true',
  from: isUtcDay,
  to: isUtcDay,
  user_id: (value: string) => UUID_RE.test(value),
} satisfies Record<string, (value: string) => boolean>;

export type FeedbackParam = keyof typeof PARAMS;
/** Only the filters that are set. from and to are UTC days; unclassified is 'true' when on. */
export type FeedbackFilters = Partial<Record<FeedbackParam, string>>;

/** The label filters, which unclassified rows can never match. */
export const LABEL_PARAMS = ['kind', 'surface', 'sentiment'] as const;

/**
 * Keeps each value the API would accept and drops the rest: an unknown label, a day that is
 * not on the calendar, a malformed member id, an empty string, a range whose from is after its
 * to, and label filters beside unclassified.
 */
function normalize(
  raw: Partial<Record<FeedbackParam, string | null | undefined>>,
): FeedbackFilters {
  const filters: FeedbackFilters = {};
  for (const [param, accepts] of Object.entries(PARAMS) as [
    FeedbackParam,
    (v: string) => boolean,
  ][]) {
    const value = raw[param];
    if (value && accepts(value)) filters[param] = value;
  }
  if (filters.from && filters.to && filters.from > filters.to) {
    delete filters.from;
    delete filters.to;
  }
  if (filters.unclassified) for (const param of LABEL_PARAMS) delete filters[param];
  return filters;
}

export function filtersFromSearch(search: URLSearchParams): FeedbackFilters {
  return normalize(Object.fromEntries(search));
}

/** The filters after a change. A null or empty value clears that filter. */
export function withFilters(
  current: FeedbackFilters,
  patch: Partial<Record<FeedbackParam, string | null>>,
): FeedbackFilters {
  return normalize({ ...current, ...patch });
}

export function feedbackParams(
  filters: FeedbackFilters,
  cursor: string | null,
): Record<string, string | number> {
  return { ...filters, ...(cursor ? { cursor } : {}), limit: 50 };
}

export function useFeedback(filters: FeedbackFilters) {
  return useInfiniteQuery<FeedbackPage, ApiError>({
    queryKey: ['admin', 'feedback', filters],
    ...AUDITED_READ,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    queryFn: ({ pageParam }) =>
      apiGet(
        '/admin/feedback',
        feedbackParams(filters, (pageParam as string | null) ?? null),
      ) as Promise<FeedbackPage>,
  });
}
